<template>
  <div class="home-page">
    <app-header />

    <main>
      <!-- Hero Section -->
      <section class="py-5 position-relative overflow-hidden">
        <!-- AI Background Elements -->
        <div class="ai-particles position-absolute w-100 h-100 top-0 start-0">
          <div class="particle particle-1"></div>
          <div class="particle particle-2"></div>
          <div class="particle particle-3"></div>
          <div class="particle particle-4"></div>
        </div>
        
        <!-- Circuit Pattern SVG Overlay -->
        <div class="circuit-pattern position-absolute w-100 h-100 top-0 start-0">
          <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
            <pattern id="circuitPattern" patternUnits="userSpaceOnUse" width="100" height="100" patternTransform="rotate(10)">
              <path d="M10,10 h80 v80 h-80 z" fill="none" stroke="rgba(47,102,169,0.07)" stroke-width="1"></path>
              <circle cx="10" cy="10" r="2" fill="rgba(47,102,169,0.1)"></circle>
              <circle cx="90" cy="90" r="2" fill="rgba(47,102,169,0.1)"></circle>
              <circle cx="90" cy="10" r="2" fill="rgba(47,102,169,0.1)"></circle>
              <circle cx="10" cy="90" r="2" fill="rgba(47,102,169,0.1)"></circle>
              <path d="M10,50 h80" stroke="rgba(47,102,169,0.05)" stroke-width="1"></path>
              <path d="M50,10 v80" stroke="rgba(47,102,169,0.05)" stroke-width="1"></path>
            </pattern>
            <rect width="100%" height="100%" fill="url(#circuitPattern)"></rect>
          </svg>
        </div>
        
        <div class="container py-5 position-relative">
          <div class="row align-items-center gy-4">
            <div class="col-lg-6 mb-5 mb-lg-0">
              <div class="ai-badge d-inline-flex align-items-center mb-3 px-3 py-2 rounded-pill bg-primary bg-opacity-10">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="#2f66a9" class="bi bi-robot me-2" viewBox="0 0 16 16">
                  <path d="M6 12.5a.5.5 0 0 1 .5-.5h3a.5.5 0 0 1 0 1h-3a.5.5 0 0 1-.5-.5M3 8.062C3 6.76 4.235 5.765 5.53 5.886a26.6 26.6 0 0 0 4.94 0C11.765 5.765 13 6.76 13 8.062v1.157a.93.93 0 0 1-.765.935c-.845.147-2.34.346-4.235.346-1.895 0-3.39-.2-4.235-.346A.93.93 0 0 1 3 9.219zm4.542-.827a.25.25 0 0 0-.217.068l-.92.9a24.8 24.8 0 0 1-1.871-.183.25.25 0 0 0-.068.495c.55.076 1.232.149 2.02.193a.25.25 0 0 0 .189-.071l.754-.736.847 1.71a.25.25 0 0 0 .404.062l.932-.97a25 25 0 0 0 1.922-.188.25.25 0 0 0-.068-.495c-.538.074-1.207.145-1.98.189a.25.25 0 0 0-.166.076l-.754.785-.842-1.7a.25.25 0 0 0-.182-.135"/>
                  <path d="M8.5 1.866a1 1 0 1 0-1 0V3h-2A4.5 4.5 0 0 0 1 7.5V8a1 1 0 0 0-1 1v2a1 1 0 0 0 1 1v1a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-1a1 1 0 0 0 1-1V9a1 1 0 0 0-1-1v-.5A4.5 4.5 0 0 0 10.5 3h-2zM14 7.5V13a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V7.5A3.5 3.5 0 0 1 5.5 4h5A3.5 3.5 0 0 1 14 7.5"/>
                </svg>
                <span class="fw-semibold">AI-Powered Conversation Practice</span>
              </div>
              
              <h1 class="display-4 fw-bold mb-4 text-primary">Free Online English Conversation Practice</h1>
              
              <div class="voice-wave-animation d-flex align-items-center mb-4">
                <div class="d-flex align-items-end me-3">
                  <div class="wave-bar"></div>
                  <div class="wave-bar"></div>
                  <div class="wave-bar"></div>
                  <div class="wave-bar"></div>
                  <div class="wave-bar"></div>
                </div>
                <p class="lead mb-0 fs-4">Boost your confidence for board exams, class presentations, and future career opportunities. Practice English speaking with our AI conversation partner – absolutely free!</p>
              </div>
              
              <div class="d-flex flex-wrap gap-3 mb-4">
                <router-link to="/conversation" class="btn btn-primary btn-lg rounded-pill shadow px-4 py-2 d-flex align-items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="currentColor" class="bi bi-mic-fill me-2" viewBox="0 0 16 16">
                    <path d="M5 3a3 3 0 0 1 6 0v5a3 3 0 0 1-6 0z"/>
                    <path d="M3.5 6.5A.5.5 0 0 1 4 7v1a4 4 0 0 0 8 0V7a.5.5 0 0 1 1 0v1a5 5 0 0 1-4.5 4.975V15h3a.5.5 0 0 1 0 1h-7a.5.5 0 0 1 0-1h3v-2.025A5 5 0 0 1 3 8V7a.5.5 0 0 1 .5-.5"/>
                  </svg>
                  Start Speaking Now
                </router-link>
                <router-link to="/learn-english/vocabulary-playground" class="btn btn-success btn-lg rounded-pill shadow px-4 py-2 d-flex align-items-center new-feature-btn position-relative">
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="currentColor" class="bi bi-book-half me-2" viewBox="0 0 16 16">
                    <path d="M8.5 2.687c.654-.689 1.782-.886 3.112-.752 1.234.124 2.503.523 3.388.893v9.923c-.918-.35-2.107-.692-3.287-.81-1.094-.111-2.278-.039-3.213.492zM8 1.783C7.015.936 5.587.81 4.287.94c-1.514.153-3.042.672-3.994 1.105A.5.5 0 0 0 0 2.5v11a.5.5 0 0 0 .707.455c.882-.4 2.303-.881 3.68-1.02 1.409-.142 2.59.087 3.223.877a.5.5 0 0 0 .78 0c.633-.79 1.814-1.019 3.222-.877 1.378.139 2.8.62 3.681 1.02A.5.5 0 0 0 16 13.5v-11a.5.5 0 0 0-.293-.455c-.952-.433-2.48-.952-3.994-1.105C10.413.809 8.985.936 8 1.783"/>
                  </svg>
                  Learn Vocabulary
                  <span class="new-badge position-absolute top-0 start-100 translate-middle badge rounded-pill bg-warning text-dark">
                    NEW
                  </span>
                </router-link>
                <a href="#how-it-helps" class="btn btn-outline-secondary btn-lg rounded-pill px-4 py-2">How It Helps</a>
              </div>
              
              <div class="student-benefits mt-4 d-flex flex-wrap">
                <div class="benefit-tag m-1 rounded-pill d-flex align-items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="#2f66a9" class="bi bi-check-circle-fill me-1" viewBox="0 0 16 16">
                    <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0m-3.97-3.03a.75.75 0 0 0-1.08.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-.01-1.05z"/>
                  </svg>
                  Improve for Board Exams
                </div>
                <div class="benefit-tag m-1 rounded-pill d-flex align-items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="#2f66a9" class="bi bi-check-circle-fill me-1" viewBox="0 0 16 16">
                    <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0m-3.97-3.03a.75.75 0 0 0-1.08.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-.01-1.05z"/>
                  </svg>
                  Boost Class Participation
                </div>
                <div class="benefit-tag m-1 rounded-pill d-flex align-items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="#2f66a9" class="bi bi-check-circle-fill me-1" viewBox="0 0 16 16">
                    <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0m-3.97-3.03a.75.75 0 0 0-1.08.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-.01-1.05z"/>
                  </svg>
                  Prepare for College Interviews
                </div>
              </div>
            </div>
            
            <div class="col-lg-6">
              <div class="position-relative">
                <!-- Digital Frame Effect -->
                <div class="digital-frame position-absolute w-100 h-100 top-0 start-0 rounded-4"></div>
                
                <!-- Main Image -->
                <div class="rounded-4 overflow-hidden shadow-lg position-relative">
                  <div class="ai-overlay position-absolute top-0 start-0 w-100 h-100 bg-gradient-primary-transparent"></div>
                  <img src="https://assets.thesupersoft.com/englishmate/heo.jpg" alt="Students practicing English conversation" class="img-fluid rounded w-100 h-auto">
                  
                  <!-- Voice Indicators -->
                  <div class="voice-indicators position-absolute bottom-0 start-0 w-100 d-flex justify-content-center pb-3">
                    <div class="voice-pill px-3 py-1 rounded-pill bg-white shadow-sm d-flex align-items-center">
                      <span class="pulse-dot me-2"></span>
                      <small class="text-primary fw-semibold">AI Listening</small>
                    </div>
                  </div>
                </div>
                
                <!-- Neural Network Decoration -->
                <div class="neural-decoration position-absolute" style="top: -15px; right: -15px; z-index: -1;">
                  <svg width="80" height="80" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="40" cy="40" r="38" stroke="#2f66a9" stroke-width="1" stroke-dasharray="4 4" fill="none"/>
                    <circle cx="40" cy="40" r="30" stroke="#2f66a9" stroke-width="1" stroke-opacity="0.5" fill="none"/>
                    <circle cx="40" cy="40" r="5" fill="#2f66a9" fill-opacity="0.2"/>
                    <circle cx="60" cy="30" r="3" fill="#2f66a9" fill-opacity="0.2"/>
                    <circle cx="65" cy="55" r="2" fill="#2f66a9" fill-opacity="0.2"/>
                    <circle cx="20" cy="25" r="2" fill="#2f66a9" fill-opacity="0.2"/>
                    <circle cx="15" cy="50" r="3" fill="#2f66a9" fill-opacity="0.2"/>
                    <line x1="40" y1="40" x2="60" y2="30" stroke="#2f66a9" stroke-width="1" stroke-opacity="0.3"/>
                    <line x1="40" y1="40" x2="65" y2="55" stroke="#2f66a9" stroke-width="1" stroke-opacity="0.3"/>
                    <line x1="40" y1="40" x2="20" y2="25" stroke="#2f66a9" stroke-width="1" stroke-opacity="0.3"/>
                    <line x1="40" y1="40" x2="15" y2="50" stroke="#2f66a9" stroke-width="1" stroke-opacity="0.3"/>
                  </svg>
                </div>
                
                <!-- Binary Code Decoration -->
                <div class="binary-decoration position-absolute bottom-0 start-0 text-primary" style="font-family: monospace; font-size: 8px; opacity: 0.1; z-index: -1;">
                  10110101<br>01001101<br>11010010<br>00101101
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- How It Helps Section -->
      <section id="how-it-helps" class="py-5 bg-white">
        <div class="container py-4">
          <h2 class="text-center mb-5 display-5 fw-bold text-primary">How EnglishMate Helps Class 9-11 Students</h2>
          <div class="row g-4">
            <div class="col-md-6 col-lg-4">
              <div class="card h-100 border-0 shadow-sm rounded-4 hover-shadow transition">
                <div class="card-body text-center p-4">
                  <div class="display-5 mb-3">📝</div>
                  <h3 class="fs-4 mb-3 fw-semibold">Excel in Board Exams</h3>
                  <p class="text-muted">Practice speaking about curriculum topics to better understand and remember concepts for your exams.</p>
                </div>
              </div>
            </div>
            <div class="col-md-6 col-lg-4">
              <div class="card h-100 border-0 shadow-sm rounded-4 hover-shadow transition">
                <div class="card-body text-center p-4">
                  <div class="display-5 mb-3">🎓</div>
                  <h3 class="fs-4 mb-3 fw-semibold">Build Academic Vocabulary</h3>
                  <p class="text-muted">Learn subject-specific terminology and expressions that will impress your teachers and classmates.</p>
                </div>
              </div>
            </div>
            <div class="col-md-6 col-lg-4">
              <div class="card h-100 border-0 shadow-sm rounded-4 hover-shadow transition">
                <div class="card-body text-center p-4">
                  <div class="display-5 mb-3">🗣️</div>
                  <h3 class="fs-4 mb-3 fw-semibold">Gain Presentation Skills</h3>
                  <p class="text-muted">Practice explaining concepts clearly and confidently for better class presentations and discussions.</p>
                </div>
              </div>
            </div>
            <div class="col-md-6 col-lg-4">
              <div class="card h-100 border-0 shadow-sm rounded-4 hover-shadow transition">
                <div class="card-body text-center p-4">
                  <div class="display-5 mb-3">🎯</div>
                  <h3 class="fs-4 mb-3 fw-semibold">Prepare for College</h3>
                  <p class="text-muted">Develop interview skills and fluency that will help you excel in college admissions.</p>
                </div>
              </div>
            </div>
            <div class="col-md-6 col-lg-4">
              <div class="card h-100 border-0 shadow-sm rounded-4 hover-shadow transition">
                <div class="card-body text-center p-4">
                  <div class="display-5 mb-3">🕒</div>
                  <h3 class="fs-4 mb-3 fw-semibold">Practice After School</h3>
                  <p class="text-muted">Fit English practice around your study schedule - available 24/7 on your phone or computer.</p>
                </div>
              </div>
            </div>
            <div class="col-md-6 col-lg-4">
              <div class="card h-100 border-0 shadow-sm rounded-4 hover-shadow transition">
                <div class="card-body text-center p-4">
                  <div class="display-5 mb-3">💯</div>
                  <h3 class="fs-4 mb-3 fw-semibold">Always 100% Free</h3>
                  <p class="text-muted">No membership fees, no trial periods, no premium features - everything is free for students.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section class="how-it-works container py-5">
        <h2 class="text-center mb-5 display-5 fw-bold text-primary">Start Practicing in 3 Simple Steps</h2>
        <div class="steps-container">
          <div class="step bg-light bg-opacity-75 rounded-4 shadow-sm">
            <div class="step-number shadow">1</div>
            <h3 class="fw-semibold">Click "Start Speaking"</h3>
            <p>No registration, no downloads - start immediately from any device.</p>
          </div>
          <div class="step bg-light bg-opacity-75 rounded-4 shadow-sm">
            <div class="step-number shadow">2</div>
            <h3 class="fw-semibold">Choose Your Topic</h3>
            <p>Practice conversations about school subjects, current events, or everyday topics.</p>
          </div>
          <div class="step bg-light bg-opacity-75 rounded-4 shadow-sm">
            <div class="step-number shadow">3</div>
            <h3 class="fw-semibold">Speak Naturally</h3>
            <p>Our AI responds like a patient teacher, helping you improve with each conversation.</p>
          </div>
        </div>
        <div class="action-center">
          <router-link to="/conversation" class="primary-btn large btn btn-primary btn-lg rounded-pill shadow px-4 py-3">Start Speaking English Now</router-link>
        </div>
      </section>

      <section class="practice-topics container my-5 py-4">
        <h2 class="text-center mb-5 display-5 fw-bold text-primary">What Can You Practice?</h2>
        <div class="topics-grid">
          <div class="topic-card rounded-4 bg-light bg-opacity-75 shadow-sm border-0">
            <div class="topic-icon shadow-sm rounded-circle bg-white p-2 d-inline-block">📚</div>
            <h3 class="fw-semibold">School Subjects</h3>
            <p>Science, History, Literature, Geography, and more - practice explaining concepts in English.</p>
          </div>
          <div class="topic-card rounded-4 bg-light bg-opacity-75 shadow-sm border-0">
            <div class="topic-icon shadow-sm rounded-circle bg-white p-2 d-inline-block">🌎</div>
            <h3 class="fw-semibold">Current Events</h3>
            <p>Discuss news, technology, environmental issues, and global developments.</p>
          </div>
          <div class="topic-card rounded-4 bg-light bg-opacity-75 shadow-sm border-0">
            <div class="topic-icon shadow-sm rounded-circle bg-white p-2 d-inline-block">👥</div>
            <h3 class="fw-semibold">Social Interactions</h3>
            <p>Practice introductions, small talk, making friends, and everyday conversations.</p>
          </div>
          <div class="topic-card rounded-4 bg-light bg-opacity-75 shadow-sm border-0">
            <div class="topic-icon shadow-sm rounded-circle bg-white p-2 d-inline-block">🎯</div>
            <h3 class="fw-semibold">Future Plans</h3>
            <p>Talk about college aspirations, career goals, and ambitions in English.</p>
          </div>
        </div>
      </section>

      <section class="testimonials container my-5 py-4">
        <h2 class="text-center mb-5 display-5 fw-bold text-primary">From Students Like You</h2>
        
        <div class="row justify-content-center mb-4">
          <div class="col-md-8 text-center">
            <div class="d-flex align-items-center justify-content-center mb-3">
              <div class="bg-dark rounded-circle p-3 shadow me-3">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="#ffffff" class="bi bi-robot" viewBox="0 0 16 16">
                  <path d="M6 12.5a.5.5 0 0 1 .5-.5h3a.5.5 0 0 1 0 1h-3a.5.5 0 0 1-.5-.5M3 8.062C3 6.76 4.235 5.765 5.53 5.886a26.6 26.6 0 0 0 4.94 0C11.765 5.765 13 6.76 13 8.062v1.157a.93.93 0 0 1-.765.935c-.845.147-2.34.346-4.235.346-1.895 0-3.39-.2-4.235-.346A.93.93 0 0 1 3 9.219zm4.542-.827a.25.25 0 0 0-.217.068l-.92.9a24.8 24.8 0 0 1-1.871-.183.25.25 0 0 0-.068.495c.55.076 1.232.149 2.02.193a.25.25 0 0 0 .189-.071l.754-.736.847 1.71a.25.25 0 0 0 .404.062l.932-.97a25 25 0 0 0 1.922-.188.25.25 0 0 0-.068-.495c-.538.074-1.207.145-1.98.189a.25.25 0 0 0-.166.076l-.754.785-.842-1.7a.25.25 0 0 0-.182-.135"/>
                  <path d="M8.5 1.866a1 1 0 1 0-1 0V3h-2A4.5 4.5 0 0 0 1 7.5V8a1 1 0 0 0-1 1v2a1 1 0 0 0 1 1v1a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-1a1 1 0 0 0 1-1V9a1 1 0 0 0-1-1v-.5A4.5 4.5 0 0 0 10.5 3h-2zM14 7.5V13a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V7.5A3.5 3.5 0 0 1 5.5 4h5A3.5 3.5 0 0 1 14 7.5"/>
                </svg>
              </div>
              <p class="fs-5 mb-0">AI-Powered Conversations, Real Student Success</p>
            </div>
          </div>
        </div>
        
        <div class="testimonials-container position-relative">
          <!-- AI Chat Interface Design -->
          <div class="row g-4">
            <!-- First Testimonial -->
            <div class="col-md-6">
              <div class="ai-chat-container bg-light rounded-4 p-3 shadow-sm border-0 h-100">
                <div class="d-flex align-items-center mb-3">
                  <div class="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center me-2" style="width: 40px; height: 40px;">
                    <span class="fw-bold">A</span>
                  </div>
                  <div>
                    <p class="fw-bold mb-0">Aarav M.</p>
                    <p class="text-secondary small mb-0">Class 10 Student, Delhi</p>
                  </div>
                </div>
                <div class="message-bubble bg-white p-3 rounded-4 shadow-sm border-start border-primary border-4 mb-2">
                  <p class="mb-0 fst-italic">EnglishMate helped me prepare for my Class 10 English board exam. I practice speaking every day and my confidence has improved a lot!</p>
                </div>
                <div class="d-flex justify-content-end">
                  <div class="pulse-dots">
                    <span class="bg-primary rounded-circle d-inline-block me-1"></span>
                    <span class="bg-primary rounded-circle d-inline-block me-1"></span>
                    <span class="bg-primary rounded-circle d-inline-block"></span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Second Testimonial -->
            <div class="col-md-6">
              <div class="ai-chat-container bg-light rounded-4 p-3 shadow-sm border-0 h-100">
                <div class="d-flex align-items-center mb-3">
                  <div class="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center me-2" style="width: 40px; height: 40px;">
                    <span class="fw-bold">E</span>
                  </div>
                  <div>
                    <p class="fw-bold mb-0">Emma L.</p>
                    <p class="text-secondary small mb-0">High School Junior, California, USA</p>
                  </div>
                </div>
                <div class="message-bubble bg-white p-3 rounded-4 shadow-sm border-start border-primary border-4 mb-2">
                  <p class="mb-0 fst-italic">As an exchange student, I needed to improve my English quickly. EnglishMate lets me practice anytime, and my teachers have noticed my improvement!</p>
                </div>
                <div class="d-flex justify-content-end">
                  <div class="pulse-dots">
                    <span class="bg-primary rounded-circle d-inline-block me-1"></span>
                    <span class="bg-primary rounded-circle d-inline-block me-1"></span>
                    <span class="bg-primary rounded-circle d-inline-block"></span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Third Testimonial -->
            <div class="col-md-6">
              <div class="ai-chat-container bg-light rounded-4 p-3 shadow-sm border-0 h-100">
                <div class="d-flex align-items-center mb-3">
                  <div class="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center me-2" style="width: 40px; height: 40px;">
                    <span class="fw-bold">J</span>
                  </div>
                  <div>
                    <p class="fw-bold mb-0">Jason T.</p>
                    <p class="text-secondary small mb-0">Grade 12 Student, New York, USA</p>
                  </div>
                </div>
                <div class="message-bubble bg-white p-3 rounded-4 shadow-sm border-start border-primary border-4 mb-2">
                  <p class="mb-0 fst-italic">I use EnglishMate to prepare for my SAT and college applications. The conversational practice helped me ace my English interviews at top universities.</p>
                </div>
                <div class="d-flex justify-content-end">
                  <div class="pulse-dots">
                    <span class="bg-primary rounded-circle d-inline-block me-1"></span>
                    <span class="bg-primary rounded-circle d-inline-block me-1"></span>
                    <span class="bg-primary rounded-circle d-inline-block"></span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Fourth Testimonial -->
            <div class="col-md-6">
              <div class="ai-chat-container bg-light rounded-4 p-3 shadow-sm border-0 h-100">
                <div class="d-flex align-items-center mb-3">
                  <div class="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center me-2" style="width: 40px; height: 40px;">
                    <span class="fw-bold">S</span>
                  </div>
                  <div>
                    <p class="fw-bold mb-0">Sofia M.</p>
                    <p class="text-secondary small mb-0">High School Student, Barcelona, Spain</p>
                  </div>
                </div>
                <div class="message-bubble bg-white p-3 rounded-4 shadow-sm border-start border-primary border-4 mb-2">
                  <p class="mb-0 fst-italic">English is my third language, and I struggled with conversation. EnglishMate gives me daily practice that fits around my busy schedule.</p>
                </div>
                <div class="d-flex justify-content-end">
                  <div class="pulse-dots">
                    <span class="bg-primary rounded-circle d-inline-block me-1"></span>
                    <span class="bg-primary rounded-circle d-inline-block me-1"></span>
                    <span class="bg-primary rounded-circle d-inline-block"></span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <!-- Decorative Elements -->
          <div class="position-absolute start-0 bottom-0 d-none d-lg-block">
            <svg width="80" height="80" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg" style="opacity: 0.1">
              <circle cx="40" cy="40" r="40" fill="#2f66a9"/>
              <path d="M20,40 Q40,20 60,40 T80,60" stroke="#ffffff" stroke-width="2" fill="none"/>
              <path d="M0,40 Q20,20 40,40 T60,60" stroke="#ffffff" stroke-width="2" fill="none"/>
            </svg>
          </div>
          <div class="position-absolute end-0 top-0 d-none d-lg-block">
            <svg width="100" height="100" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg" style="opacity: 0.1">
              <rect x="20" y="20" width="60" height="60" rx="10" fill="#2f66a9"/>
              <circle cx="40" cy="40" r="5" fill="#ffffff"/>
              <circle cx="60" cy="40" r="5" fill="#ffffff"/>
              <path d="M35,60 Q50,70 65,60" stroke="#ffffff" stroke-width="2" fill="none"/>
            </svg>
          </div>
        </div>

        <!-- Voice Wave Animation -->
        <div class="row justify-content-center mt-5">
          <div class="col-md-6 text-center">
            <div class="d-flex justify-content-center align-items-center gap-1 mb-3">
              <div class="bg-primary rounded" style="height: 15px; width: 3px;"></div>
              <div class="bg-primary rounded" style="height: 25px; width: 3px;"></div>
              <div class="bg-primary rounded" style="height: 35px; width: 3px;"></div>
              <div class="bg-primary rounded" style="height: 25px; width: 3px;"></div>
              <div class="bg-primary rounded" style="height: 15px; width: 3px;"></div>
              <div class="ms-2 fw-bold">AI Voice Technology</div>
            </div>
            <p class="text-secondary small">Join thousands of students improving their English speaking skills</p>
          </div>
        </div>
      </section>

      <section class="faq-section container my-5 py-4">
        <h2 class="text-center mb-5 display-5 fw-bold text-primary">Questions Students Ask</h2>
        <div class="faq-container bg-light p-4 rounded-4 shadow-sm">
          <div class="faq-item">
            <h3 class="text-primary fw-bold">Is EnglishMate really free for students?</h3>
            <p>Yes, EnglishMate is completely free for all students. No payment information required, ever.</p>
          </div>
          <div class="faq-item">
            <h3 class="text-primary fw-bold">Do I need to create an account?</h3>
            <p>No registration needed! Just open the website and start practicing immediately.</p>
          </div>
          <div class="faq-item">
            <h3 class="text-primary fw-bold">Will this help with my English subject in school?</h3>
            <p>Absolutely! Regular practice improves your speaking skills, vocabulary, and confidence - all of which help in school exams and assessments.</p>
          </div>
          <div class="faq-item">
            <h3 class="text-primary fw-bold">Can I use EnglishMate on my phone?</h3>
            <p>Yes, EnglishMate works on smartphones, tablets, and computers - practice anywhere with internet access.</p>
          </div>
          <div class="faq-item border-0 pb-0 mb-0">
            <h3 class="text-primary fw-bold">How much should I practice to see improvement?</h3>
            <p class="mb-0">Even 10-15 minutes of practice a few times per week can make a big difference in your English fluency and confidence.</p>
          </div>
        </div>
      </section>

      <section class="cta-section bg-dark bg-gradient">
        <div class="container py-5">
          <h2 class="text-center mb-4 display-5 fw-bold text-white">Ready to Improve Your English Speaking Skills?</h2>
          <p class="text-center text-white fs-5 mb-4">Join thousands of Class 9-11 students who are boosting their English confidence with free online practice</p>
          <div class="text-center">
            <router-link to="/conversation" class="primary-btn large btn btn-light text-dark btn-lg rounded-pill shadow px-5 py-3 fw-bold">Start Your Free Practice Now</router-link>
          </div>
        </div>
      </section>
    </main>
    
    <app-footer />
  </div>
</template>

<script>
import AppHeader from '../components/AppHeader.vue'
import AppFooter from '../components/AppFooter.vue'

export default {
  name: 'HomePage',
  components: {
    AppHeader,
    AppFooter
  },
  metaInfo: {
    title: 'Free Online English Conversation Practice | EnglishMate',
    meta: [
      { name: 'description', content: 'Practice English conversation online for free with our AI-powered speaking partner. Improve your fluency, pronunciation, and confidence without registration.' },
      { name: 'keywords', content: 'free online English conversation practice, English speaking practice free, practice English conversation, online English speaking, free English practice' },
      { property: 'og:title', content: 'Free Online English Conversation Practice | EnglishMate' },
      { property: 'og:description', content: 'Practice English conversation online for free with our AI speaking partner. No registration required, available 24/7 for students and learners.' },
      { property: 'og:type', content: 'website' },
      { property: 'og:url', content: 'https://englishmate.io' },
      { property: 'og:image', content: 'https://assets.thesupersoft.com/englishmate/englishmate-social.png' },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: 'Free Online English Conversation Practice' },
      { name: 'twitter:description', content: 'Practice English conversation online for free with our AI-powered speaking partner. Improve your English speaking skills anytime, anywhere.' },
      { name: 'twitter:image', content: 'https://assets.thesupersoft.com/englishmate/englishmate-social.png' },
      { name: 'robots', content: 'index, follow' },
      { name: 'language', content: 'English' }
    ]
  }
}
</script>

<style scoped>
.home-page {
  width: 100%;
  max-width: 100%;
  overflow-x: hidden;
}

.btn-primary {
  background-color: #2f66a9;
  border-color: #2f66a9;
}

.btn-primary:hover {
  background-color: #245089;
  border-color: #245089;
}

.btn-outline-secondary:hover {
  background-color: rgba(47,102,169,0.1);
  color: #2f66a9;
  border-color: #2f66a9;
}

.student-benefits {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 1rem;
}

.benefit-tag {
  background-color: rgba(47,102,169,0.1);
  color: #2f66a9;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 0.9rem;
  font-weight: 500;
}

.how-it-works h2, .testimonials h2, .faq-section h2, .cta-section h2, .practice-topics h2 {
  text-align: center;
  margin-bottom: 3rem;
  font-size: 2.2rem;
  color: #1a1a1a;
}

.steps-container {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  margin-bottom: 3rem;
}

.step {
  flex: 1;
  min-width: 250px;
  padding: 1.5rem;
  text-align: center;
  margin-bottom: 1.5rem;
}

.step-number {
  width: 50px;
  height: 50px;
  line-height: 50px;
  border-radius: 50%;
  background-color: #2f66a9;
  color: white;
  font-size: 1.5rem;
  font-weight: bold;
  margin: 0 auto 1rem;
}

.action-center {
  text-align: center;
  margin: 2rem 0;
}

.topics-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1.5rem;
  margin-top: 2rem;
}

.topic-card {
  background-color: #f8f9fa;
  border-radius: 10px;
  padding: 1.5rem;
  box-shadow: 0 2px 15px rgba(0,0,0,0.05);
  transition: transform 0.2s;
}

.topic-card:hover {
  transform: translateY(-5px);
}

.topic-icon {
  font-size: 2rem;
  margin-bottom: 1rem;
}

.testimonials-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 2rem;
}

.testimonial-card {
  background-color: #fff;
  border-radius: 10px;
  padding: 2rem;
  box-shadow: 0 3px 15px rgba(0,0,0,0.1);
  position: relative;
}

.quote-mark {
  position: absolute;
  top: 10px;
  left: 15px;
  font-size: 3rem;
  color: rgba(47,102,169,0.1);
  font-family: serif;
}

.testimonial-text {
  margin-bottom: 1.5rem;
  font-style: italic;
}

.author-name {
  font-weight: bold;
  margin-bottom: 0.2rem;
}

.author-info {
  color: #6c757d;
  font-size: 0.9rem;
}


.faq-item {
  margin-bottom: 1.5rem;
  padding-bottom: 1.5rem;
  border-bottom: 1px solid #e9ecef;
}

.faq-item h3 {
  color: #2f66a9;
  margin-bottom: 0.75rem;
  font-size: 1.2rem;
}

.cta-section {
  background-color: #2f66a9;
  color: white;
  text-align: center;
  padding: 4rem 1rem;
  margin-top: 4rem;
}

.cta-section h2 {
  color: white;
  margin-bottom: 1rem;
}

.cta-section p {
  margin-bottom: 2rem;
  font-size: 1.1rem;
}

.cta-section .primary-btn {
  background-color: white;
  color: #2f66a9;
  border: none;
  padding: 0.75rem 2rem;
  font-size: 1.1rem;
  border-radius: 50px;
  display: inline-block;
  text-decoration: none;
  font-weight: 600;
  transition: all 0.2s;
}

.cta-section .primary-btn:hover {
  background-color: #f8f9fa;
  transform: translateY(-3px);
  box-shadow: 0 5px 15px rgba(0,0,0,0.1);
}

@media (max-width: 768px) {
  .steps-container {
    flex-direction: column;
  }
  
  .step {
    width: 100%;
  }
  
  .topics-grid, .testimonials-grid {
    grid-template-columns: 1fr;
  }
}

/* AI Hero Section Styles */
.hero-section {
  background-color: #f8f9fa;
  position: relative;
  overflow: hidden;
}

/* Circuit Pattern */
.circuit-pattern {
  pointer-events: none;
  opacity: 0.4;
}

/* AI Badge */
.ai-badge {
  animation: fadeIn 1s ease-out;
}

/* Digital Frame */
.digital-frame {
  border: 2px dashed rgba(47,102,169,0.2);
  border-radius: 1rem;
  animation: borderPulse 4s infinite;
}

@keyframes borderPulse {
  0%, 100% { border-color: rgba(47,102,169,0.2); }
  50% { border-color: rgba(47,102,169,0.5); }
}

/* AI Overlay */
.bg-gradient-primary-transparent {
  background: linear-gradient(45deg, rgba(47,102,169,0.1) 0%, rgba(0,123,255,0) 100%);
}

/* Voice Wave Animation */
.wave-bar {
  width: 4px;
  height: 15px;
  margin: 0 2px;
  background-color: #2f66a9;
  border-radius: 5px;
  animation: waveAnimation 1.5s infinite;
}

.wave-bar:nth-child(1) {
  animation-delay: 0s;
  height: 10px;
}
.wave-bar:nth-child(2) {
  animation-delay: 0.2s;
  height: 15px;
}
.wave-bar:nth-child(3) {
  animation-delay: 0.4s;
  height: 20px;
}
.wave-bar:nth-child(4) {
  animation-delay: 0.6s;
  height: 15px;
}
.wave-bar:nth-child(5) {
  animation-delay: 0.8s;
  height: 10px;
}

@keyframes waveAnimation {
  0%, 100% { transform: scaleY(1); }
  50% { transform: scaleY(1.5); }
}

/* Pulse Dot */
.pulse-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: #2f66a9;
  position: relative;
  display: inline-block;
}

.pulse-dot::after {
  content: '';
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  background-color: #2f66a9;
  border-radius: 50%;
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0% {
    transform: scale(1);
    opacity: 1;
  }
  100% {
    transform: scale(3);
    opacity: 0;
  }
}

/* AI Particles */
.particle {
  position: absolute;
  background-color: rgba(47,102,169,0.1);
  border-radius: 50%;
}

.particle-1 {
  width: 150px;
  height: 150px;
  top: -50px;
  left: -50px;
  animation: floatAnimation 10s infinite ease-in-out;
}

.particle-2 {
  width: 100px;
  height: 100px;
  bottom: -30px;
  right: 10%;
  animation: floatAnimation 8s infinite ease-in-out reverse;
}

.particle-3 {
  width: 50px;
  height: 50px;
  top: 30%;
  right: -20px;
  animation: floatAnimation 6s infinite ease-in-out;
}

.particle-4 {
  width: 75px;
  height: 75px;
  bottom: 20%;
  left: 15%;
  animation: floatAnimation 12s infinite ease-in-out reverse;
}

@keyframes floatAnimation {
  0%, 100% { transform: translate(0, 0) rotate(0deg); }
  25% { transform: translate(10px, -10px) rotate(5deg); }
  50% { transform: translate(0, 15px) rotate(0deg); }
  75% { transform: translate(-10px, -5px) rotate(-5deg); }
}

/* Typing Indicator in Testimonials */
.pulse-dots span {
  width: 6px;
  height: 6px;
  display: inline-block;
  animation: pulseTyping 1.5s infinite ease-in-out;
}

.pulse-dots span:nth-child(2) {
  animation-delay: 0.2s;
}

.pulse-dots span:nth-child(3) {
  animation-delay: 0.4s;
}

@keyframes pulseTyping {
  0%, 100% { transform: scale(1); opacity: 0.5; }
  50% { transform: scale(1.2); opacity: 1; }
}

/* Fade In Animation */
@keyframes fadeIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}

/* General Animations */
.home-page h1,
.home-page .lead,
.home-page .d-flex.flex-wrap.gap-3,
.home-page .student-benefits {
  animation: fadeIn 0.8s ease-out forwards;
}

.home-page .lead {
  animation-delay: 0.2s;
}

.home-page .d-flex.flex-wrap.gap-3 {
  animation-delay: 0.4s;
}

.home-page .student-benefits {
  animation-delay: 0.6s;
}

/* Voice Pill */
.voice-pill {
  animation: fadeIn 1s ease-out 0.8s forwards;
  opacity: 0;
}

/* New Feature Button Animations */
.new-feature-btn {
  position: relative;
  overflow: hidden;
  animation: gentleBounce 2s ease-in-out infinite;
}

.new-feature-btn::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent);
  transition: left 0.5s;
}

.new-feature-btn:hover::before {
  left: 100%;
}

.new-badge {
  animation: badgePulse 1.5s ease-in-out infinite;
  font-size: 0.6rem;
  font-weight: bold;
  padding: 0.25rem 0.5rem;
  box-shadow: 0 2px 8px rgba(255, 193, 7, 0.3);
}

@keyframes gentleBounce {
  0%, 100% { 
    transform: translateY(0) scale(1); 
  }
  50% { 
    transform: translateY(-2px) scale(1.02); 
  }
}

@keyframes badgePulse {
  0%, 100% { 
    transform: scale(1); 
    box-shadow: 0 2px 8px rgba(255, 193, 7, 0.3); 
  }
  50% { 
    transform: scale(1.1); 
    box-shadow: 0 4px 15px rgba(255, 193, 7, 0.5); 
  }
}

.new-feature-btn:hover {
  transform: translateY(-3px);
  box-shadow: 0 8px 25px rgba(40, 167, 69, 0.3);
  transition: all 0.3s ease;
}

/* Responsive adjustments */
@media (max-width: 992px) {
  .neural-decoration, .binary-decoration {
    display: none;
  }
}
</style> 